// =====================================================
// Two-Factor Setup Component
// =====================================================
//
// INSTRUCTIONS:
// 1. Create this component in your components folder
// 2. Add to your app.module.ts declarations
// 3. Add route or include in user settings/profile page
//
// This component handles enabling/disabling 2FA and managing trusted devices.
//

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService, TwoFactorSetupResponse, TrustedDevice } from '@app/_services';

type SetupStep = 'status' | 'qrcode' | 'verify' | 'backup' | 'complete';

@Component({
  selector: 'app-two-factor-setup',
  templateUrl: './two-factor-setup.component.html',
  styleUrls: ['./two-factor-setup.component.css']
})
export class TwoFactorSetupComponent implements OnInit {
  // State
  loading = false;
  error = '';
  success = '';

  // 2FA Status
  totpEnabled = false;
  trustedDevicesCount = 0;
  hasBackupCodes = false;

  // Setup flow
  currentStep: SetupStep = 'status';
  setupData: TwoFactorSetupResponse | null = null;
  verifyForm: FormGroup;
  backupCodes: string[] = [];

  // Disable flow
  disableForm: FormGroup;
  showDisableModal = false;

  // Trusted devices
  trustedDevices: TrustedDevice[] = [];
  showDevicesModal = false;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthenticationService
  ) { }

  ngOnInit(): void {
    this.verifyForm = this.formBuilder.group({
      code: ['', [Validators.required, Validators.minLength(6)]]
    });

    this.disableForm = this.formBuilder.group({
      password: ['', Validators.required]
    });

    this.loadStatus();
  }

  // Load current 2FA status
  loadStatus(): void {
    this.loading = true;
    this.authService.get2FAStatus().subscribe({
      next: (status) => {
        this.totpEnabled = status.totp_enabled;
        this.trustedDevicesCount = status.trusted_devices_count;
        this.hasBackupCodes = status.has_backup_codes;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Nu s-a putut încărca starea 2FA';
        this.loading = false;
      }
    });
  }

  // Start 2FA setup
  startSetup(): void {
    this.loading = true;
    this.error = '';

    this.authService.setup2FA().subscribe({
      next: (data) => {
        this.setupData = data;
        this.currentStep = 'qrcode';
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Nu s-a putut iniția configurarea 2FA';
        this.loading = false;
      }
    });
  }

  // Proceed to verification step
  proceedToVerify(): void {
    this.currentStep = 'verify';
  }

  // Verify first code and complete setup
  verifySetup(): void {
    if (this.verifyForm.invalid || !this.setupData) return;

    this.loading = true;
    this.error = '';

    this.authService.verify2FASetup(
      this.setupData.setup_token,
      this.verifyForm.value.code
    ).subscribe({
      next: (result) => {
        this.backupCodes = result.backup_codes;
        this.currentStep = 'backup';
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Cod invalid. Verificați și încercați din nou.';
        this.loading = false;
        this.verifyForm.patchValue({ code: '' });
      }
    });
  }

  // Complete setup
  completeSetup(): void {
    this.currentStep = 'complete';
    this.totpEnabled = true;
    this.hasBackupCodes = true;

    // Clear sensitive data
    this.setupData = null;
    this.backupCodes = [];
    this.verifyForm.reset();
  }

  // Back to status
  backToStatus(): void {
    this.currentStep = 'status';
    this.setupData = null;
    this.backupCodes = [];
    this.verifyForm.reset();
    this.loadStatus();
  }

  // Copy backup codes
  copyBackupCodes(): void {
    const text = this.backupCodes.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      this.success = 'Codurile au fost copiate!';
      setTimeout(() => this.success = '', 3000);
    });
  }

  // Download backup codes
  downloadBackupCodes(): void {
    const text = 'Doctor Business - Coduri de Rezervă 2FA\n' +
      '==========================================\n\n' +
      'Păstrați aceste coduri în siguranță!\n' +
      'Fiecare cod poate fi folosit o singură dată.\n\n' +
      this.backupCodes.join('\n');

    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'doctor-business-backup-codes.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  // Show disable modal
  openDisableModal(): void {
    this.showDisableModal = true;
    this.disableForm.reset();
    this.error = '';
  }

  // Close disable modal
  closeDisableModal(): void {
    this.showDisableModal = false;
    this.disableForm.reset();
  }

  // Disable 2FA
  disable2FA(): void {
    if (this.disableForm.invalid) return;

    this.loading = true;
    this.error = '';

    this.authService.disable2FA(this.disableForm.value.password).subscribe({
      next: () => {
        this.totpEnabled = false;
        this.hasBackupCodes = false;
        this.trustedDevicesCount = 0;
        this.showDisableModal = false;
        this.success = 'Autentificarea în doi pași a fost dezactivată.';
        this.loading = false;
        setTimeout(() => this.success = '', 5000);
      },
      error: (err) => {
        this.error = 'Parola incorectă';
        this.loading = false;
      }
    });
  }

  // Load and show trusted devices
  showTrustedDevices(): void {
    this.loading = true;
    this.authService.getTrustedDevices().subscribe({
      next: (result) => {
        this.trustedDevices = result.devices;
        this.showDevicesModal = true;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Nu s-au putut încărca dispozitivele';
        this.loading = false;
      }
    });
  }

  // Close devices modal
  closeDevicesModal(): void {
    this.showDevicesModal = false;
  }

  // Revoke a device
  revokeDevice(deviceId: number): void {
    if (!confirm('Sigur doriți să revocați acest dispozitiv?')) return;

    this.authService.revokeDevice(deviceId).subscribe({
      next: () => {
        this.trustedDevices = this.trustedDevices.filter(d => d.id !== deviceId);
        this.trustedDevicesCount--;
        this.success = 'Dispozitivul a fost revocat.';
        setTimeout(() => this.success = '', 3000);
      },
      error: (err) => {
        this.error = 'Nu s-a putut revoca dispozitivul';
      }
    });
  }

  // Revoke all devices
  revokeAllDevices(): void {
    if (!confirm('Sigur doriți să revocați toate dispozitivele? Veți fi deconectat de pe celelalte dispozitive.')) return;

    this.authService.revokeAllDevices(true).subscribe({
      next: (result) => {
        this.trustedDevices = [];
        this.trustedDevicesCount = 1; // Current device remains
        this.success = `${result.revoked_count} dispozitive au fost revocate.`;
        this.showDevicesModal = false;
        setTimeout(() => this.success = '', 3000);
      },
      error: (err) => {
        this.error = 'Nu s-au putut revoca dispozitivele';
      }
    });
  }

  // Format date for display
  formatDate(dateStr: string): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  copySecret(): void {
    if (this.setupData?.secret) {
      navigator.clipboard.writeText(this.setupData.secret).then(() => {
        this.success = 'Codul secret a fost copiat!';
        setTimeout(() => this.success = '', 3000);
      });
    }
  }
}
